import { cn } from "@/common/utils/cn";
import AnimatedCountdown from "@/features/maintenance/features/auctions/components/AnimatedCountdown";
import { BanIcon, CheckCircle2Icon, PauseIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useServerTimeOffset } from "@shared/hooks/useServerTime";

interface AuctionTimerProps {
  startDate: Date;
  endDate: Date;
  isPaused?: boolean;
  isFinished?: boolean;
  isCanceled?: boolean;
}

const AuctionTimer = ({
  startDate,
  endDate,
  isPaused = false,
  isFinished = false,
  isCanceled = false,
}: AuctionTimerProps) => {
  const offsetMs = useServerTimeOffset();
  const [clientNow, setClientNow] = useState(() => Date.now());
  const now = clientNow + offsetMs;

  const totalMs = endDate.getTime() - startDate.getTime();
  const elapsedMs = now - startDate.getTime();
  const rawRemainingMs = endDate.getTime() - now;
  const remainingMs = Math.max(0, rawRemainingMs);
  
  const effectiveIsFinished = isFinished || rawRemainingMs < 0;
  const isStopped = effectiveIsFinished || isCanceled;

  const size = 22 * 4;
  const strokeWidth = 4;
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const progress = totalMs > 0 ? Math.min(elapsedMs / totalMs, 1) : 1;
  const progressOffset = progress * circumference;

  useEffect(() => {
    if (remainingMs <= 0 || isStopped || isPaused) return;

    const interval = setInterval(() => {
      setClientNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, [remainingMs, isStopped, isPaused]);

  const formatTime = (totalMs: number) => {
    const totalSeconds = Math.floor(totalMs / 1000);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    return `${hrs.toString().padStart(2, '0')}:${mins
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="relative size-[88px] flex items-center justify-center bg-neutral-700 rounded-full shadow-xl">
      <div className="relative flex items-center justify-center">
        <svg
          width={size}
          height={size}
          className="transform rotate-90 absolute"
        >
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="transparent"
            className="text-neutral-600"
          />

          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={!isStopped ? progressOffset : undefined}
            className={cn(
              'text-yellow-500',
              !isPaused && !isStopped && 'text-blue-700',
              effectiveIsFinished && 'text-success',
              isCanceled && 'text-danger',
            )}
            style={{ transition: 'stroke-dashoffset 1s linear' }}
          />
        </svg>

        <div className="z-10 flex flex-col items-center justify-center gap-1 select-none">
          {isCanceled ? (
            <>
              <div className="py-0.5" />

              <span className="text-[11px] leading-none font-bold text-danger text-center">
                Cancelado
              </span>

              <BanIcon
                className="text-danger"
                size={18}
                strokeWidth={1.5}
              />
            </>
          ) : effectiveIsFinished ? (
            <>
              <div className="py-0.5" />

              <span className="text-[11px] leading-none font-bold text-success-tertiary text-center">
                Finalizado
              </span>

              <CheckCircle2Icon
                className="text-success"
                size={18}
                strokeWidth={1.5}
              />
            </>
          ) : isPaused ? (
            <>
              <div className="py-0.5" />

              <span className="text-[11px] leading-none font-bold text-warning text-center">
                Pausado
              </span>

              <PauseIcon
                className="text-warning"
                size={18}
                strokeWidth={1.5}
              />
            </>
          ) : (
            <AnimatedCountdown
              value={formatTime(remainingMs)}
              className="text-[13px] font-bold text-white"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default AuctionTimer;
